import { useContext } from 'react';
import { SafeContext } from '../context/SafeContext';

/**
 * Top-of-app banner driven by SafeContext.transientBannerMessage.
 * Currently used only by the entitlement-error callback in client.ts —
 * when a 4xx with EntitlementErrorBody arrives, SafeContext writes a
 * generic "your state changed, try again" message here. The user
 * dismisses by clicking ×, or any new entitlement-error overwrites
 * (which is fine — they're all the same message).
 *
 * Suppressed for the stable uncovered states `free` and `lapsed-member`:
 * "try again" is misleading for both (a free user won't change by retrying; a
 * lapsed member's family owner must renew — retrying does nothing). The
 * FreeAccountBanner / LapsedMemberBanner carry the right persistent message
 * instead. Gating here (rather than at the handler) is self-correcting: once a
 * state change settles to one of these, this banner hides on the next render
 * without needing the handler to have known.
 */
const STABLE_UNCOVERED_STATES = new Set(['free', 'lapsed-member']);

export function TransientBanner() {
  const ctx = useContext(SafeContext);
  if (!ctx) return null;
  const { transientBannerMessage, dismissTransientBanner, entitlement } = ctx;
  if (!transientBannerMessage) return null;
  if (entitlement?.state && STABLE_UNCOVERED_STATES.has(entitlement.state)) return null;

  return (
    <div className="bg-amber-100 border-b border-amber-400 px-4 py-2 flex items-center justify-between gap-4">
      <span className="text-sm text-amber-950">{transientBannerMessage}</span>
      <button
        id="transient-banner-dismiss-button"
        type="button"
        onClick={dismissTransientBanner}
        aria-label="Dismiss"
        className="text-amber-950 hover:text-amber-700 text-sm"
      >
        ×
      </button>
    </div>
  );
}
